import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MAIL_NOT_DELIVERED_SNACKBAR } from '../../shared/invitation-mail-snackbar';
import { CustomerUserDTO, UpdateTenantUserStatusRequest } from '../../models/customer.model';
import { FIELD_LENGTHS } from '../../shared/field-lengths';
import { TenantUserService } from '../../services/tenant-user.service';
import { AvatarComponent } from '../avatar/avatar.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { VerwaltungStore } from '../verwaltung/verwaltung.store';
import { VwKopfComponent } from '../verwaltung/vw-kopf.component';
import {
  Aktionen,
  AnzeigeStatus,
  RollenSperre,
  StatusFilter,
  aktionen,
  anzeigeStatus,
  avatarName,
  passtZuFilter,
  rollenSchluessel,
  rollenSperre,
  sortiere,
  zaehle,
} from './verwaltung-benutzer.mapping';

type Status = UpdateTenantUserStatusRequest['status'];

interface Zeile {
  id: string;
  person: CustomerUserDTO;
  name: string;
  avatar: string;
  email: string;
  eigene: boolean;
  rolle: string;
  status: AnzeigeStatus;
  aktionen: Aktionen;
  rollenSperre: RollenSperre | null;
}

const FEHLER_SNACKBAR = { panelClass: 'snackbar-error' };

const zeilenAktion = (id: string, aktion: 'aktivieren' | 'deaktivieren') =>
  `[data-zeile-id="${id}"] [data-aktion="${aktion}"]`;

@Component({
  selector: 'app-verwaltung-benutzer',
  standalone: true,
  imports: [AvatarComponent, ConfirmDialogComponent, MatTooltip, RouterLink, TranslatePipe, VwKopfComponent],
  templateUrl: './verwaltung-benutzer.component.html',
  styleUrl: './verwaltung-benutzer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VerwaltungBenutzerComponent {
  readonly store = inject(VerwaltungStore);
  private readonly tenantUserService = inject(TenantUserService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly injector = inject(Injector);

  readonly fieldLengths = FIELD_LENGTHS;
  readonly statusWerte: AnzeigeStatus[] = ['aktiv', 'eingeladen', 'deaktiviert'];

  private readonly suchfeld = viewChild<ElementRef<HTMLInputElement>>('suchfeld');

  readonly suche = signal('');
  readonly statusFilter = signal<StatusFilter>('alle');
  readonly frage = signal<Zeile | null>(null);
  readonly laeuft = signal<ReadonlySet<string>>(new Set());
  /** Alte und neue Einladungs-ID: Der Resend ersetzt die Einladung, die Zeile bleibt nach dem Neuladen gesperrt. */
  readonly gesendet = signal<ReadonlySet<string>>(new Set());

  /** Status aus der Antwort des Servers, bis der Store die Liste neu geladen hat. */
  private readonly geaendert = linkedSignal<CustomerUserDTO[] | null, ReadonlyMap<string, Status>>({
    source: this.store.personen,
    computation: () => new Map(),
  });

  /** Ohne Zustellung ersetzte Einladung: Ein zweiter Resend auf die alte ID scheitert, bis die Liste neu geladen ist. */
  private readonly ersetzt = linkedSignal<CustomerUserDTO[] | null, ReadonlySet<string>>({
    source: this.store.personen,
    computation: () => new Set(),
  });

  private readonly personen = computed(() => {
    const geaendert = this.geaendert();
    return (this.store.personen() ?? []).map((p) => {
      const status = geaendert.get(p.id);
      return status ? { ...p, status } : p;
    });
  });

  readonly kennzahlen = computed(() =>
    this.store.personen() === null ? null : { ...zaehle(this.personen()), eingeladen: this.store.eingeladen() ?? 0 },
  );

  readonly filterAktiv = computed(() => this.suche() !== '' || this.statusFilter() !== 'alle');

  readonly zeilen = computed<Zeile[]>(() => {
    const angemeldet = { id: this.store.angemeldeteId(), rolle: this.store.angemeldeteRolle() };
    return sortiere(this.personen(), angemeldet.id)
      .filter((p) => passtZuFilter(p, this.suche(), this.statusFilter()))
      .map((p) => ({
        id: p.id,
        person: p,
        name: p.displayName,
        avatar: avatarName(p),
        email: p.email,
        eigene: p.id === angemeldet.id,
        rolle: rollenSchluessel(p.role),
        status: anzeigeStatus(p),
        aktionen: aktionen(p, angemeldet),
        rollenSperre: rollenSperre(p, angemeldet),
      }));
  });

  readonly frageLaeuft = computed(() => {
    const frage = this.frage();
    return frage !== null && this.laeuft().has(frage.id);
  });

  kennzahlWert(k: AnzeigeStatus): string {
    const kennzahlen = this.kennzahlen();
    return kennzahlen === null ? '–' : String(kennzahlen[k]);
  }

  ersetztOhneZustellung(id: string): boolean {
    return this.ersetzt().has(id);
  }

  istGesperrt(id: string): boolean {
    return this.laeuft().has(id) || this.ersetzt().has(id);
  }

  statusFilterSetzen(wert: string): void {
    this.statusFilter.set(wert as StatusFilter);
  }

  filterZuruecksetzen(): void {
    this.suche.set('');
    this.statusFilter.set('alle');
    this.suchfeld()?.nativeElement.focus();
  }

  deaktivierenFragen(zeile: Zeile): void {
    if (!this.istGesperrt(zeile.id)) {
      this.frage.set(zeile);
    }
  }

  deaktivierenBestaetigen(): void {
    const zeile = this.frage();
    if (zeile) {
      this.statusSetzen(zeile, 'INACTIVE');
    }
  }

  aktivieren(zeile: Zeile): void {
    this.statusSetzen(zeile, 'ACTIVE');
  }

  erneutSenden(zeile: Zeile): void {
    const customerId = this.store.customerId();
    if (!customerId || this.istGesperrt(zeile.id) || this.gesendet().has(zeile.id)) {
      return;
    }
    this.starten(zeile.id);
    this.tenantUserService.resendInvitation(customerId, zeile.id).subscribe({
      next: (einladung) => {
        this.beenden(zeile.id);
        if (einladung.mailDelivered === false) {
          this.ersetzt.update((ids) => new Set([...ids, zeile.id]));
          this.snackBar.open(
            this.translate.instant('verwaltung.benutzer.meldung.mailNichtZugestellt'),
            undefined,
            MAIL_NOT_DELIVERED_SNACKBAR,
          );
        } else {
          this.gesendet.update((ids) => new Set([...ids, zeile.id, einladung.id]));
        }
        this.store.personenAktualisieren();
      },
      error: (fehler: HttpErrorResponse) => {
        this.beenden(zeile.id);
        if (fehler.status === 409) {
          this.melden('verwaltung.benutzer.meldung.einladungNichtOffen');
          this.store.personenAktualisieren();
        } else {
          this.melden(
            fehler.status === 429
              ? 'verwaltung.benutzer.meldung.zuVieleVersuche'
              : 'verwaltung.benutzer.meldung.sendenFehler',
          );
        }
      },
    });
  }

  private statusSetzen(zeile: Zeile, status: Status): void {
    const customerId = this.store.customerId();
    if (!customerId || this.istGesperrt(zeile.id)) {
      return;
    }
    this.starten(zeile.id);
    this.tenantUserService.setStatus(customerId, zeile.id, status).subscribe({
      next: () => {
        this.beenden(zeile.id);
        this.frage.set(null);
        this.geaendert.update((alt) => new Map([...alt, [zeile.id, status]]));
        // Der geklickte Button verschwindet mit dem Statuswechsel; der Fokus soll in der Zeile bleiben.
        this.fokussieren(zeilenAktion(zeile.id, status === 'ACTIVE' ? 'deaktivieren' : 'aktivieren'));
        this.store.personenAktualisieren();
      },
      error: (fehler: HttpErrorResponse) => {
        this.beenden(zeile.id);
        this.frage.set(null);
        this.melden(
          fehler.status === 409 && fehler.error?.code === 'LAST_TENANT_ADMIN'
            ? 'verwaltung.benutzer.meldung.letzterAdmin'
            : 'verwaltung.benutzer.meldung.statusFehler',
        );
      },
    });
  }

  rollenOeffnen(zeile: Zeile): void {
    if (zeile.rollenSperre === null) {
      void this.router.navigate(['/settings', 'benutzer', zeile.id, 'bearbeiten'], { queryParams: { tab: 'rolle' } });
    }
  }

  private fokussieren(selektor: string): void {
    afterNextRender(() => this.host.nativeElement.querySelector<HTMLElement>(selektor)?.focus(), {
      injector: this.injector,
    });
  }

  private starten(id: string): void {
    this.laeuft.update((ids) => new Set([...ids, id]));
  }

  private beenden(id: string): void {
    this.laeuft.update((ids) => new Set([...ids].filter((i) => i !== id)));
  }

  private melden(schluessel: string): void {
    this.snackBar.open(this.translate.instant(schluessel), undefined, FEHLER_SNACKBAR);
  }
}
